import { Phone, MapPin, Clock, ArrowUp, Facebook, Mail } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_7a187c08-1566-4a5d-94a6-902036d37fa8/artifacts/oin9oc2t_Untitled%20design%20%2814%29.png";
const FACEBOOK_URL = "https://www.facebook.com/share/18aF5rQHCS/?mibextid=wwXIfr";

const quickLinks = [
  { label: 'Home', href: '#hero' },
  { label: 'Services', href: '#services' },
  { label: 'Pickup & Delivery', href: '#how-it-works' },
  { label: 'Commercial', href: '#commercial' },
  { label: 'About', href: '#about' },
  { label: 'FAQ', href: '#faq' },
  { label: 'Contact', href: '#contact' },
];

const serviceLinks = [
  'Self-Service Laundry',
  'Wash, Dry & Fold',
  'Pickup & Delivery',
  'Ironing & Pressing',
  'Alterations',
  'Commercial Laundry',
];

export default function Footer() {
  const scrollTo = (e, href) => {
    e.preventDefault();
    const el = document.querySelector(href);
    if (el) {
      const offset = 80;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  const scrollToTop = (e) => {
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="bg-poly-blue-dark pt-16 pb-8 relative overflow-hidden" data-testid="footer">
      {/* Logo watermark */}
      <img src={LOGO_URL} alt="" className="logo-watermark absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 opacity-[0.04]" aria-hidden="true" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 relative z-10">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
          {/* Brand */}
          <div className="sm:col-span-2 lg:col-span-1">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-white rounded-full p-1.5 shadow-lg">
                <img src={LOGO_URL} alt="Poly Clean Center" className="w-14 h-14 object-contain" loading="lazy" />
              </div>
              <div>
                <span className="font-heading font-bold text-xl text-white block leading-tight">Poly Clean</span>
                <span className="text-white/60 text-xs tracking-wider uppercase">Center Laundromat</span>
              </div>
            </div>
            <p className="text-white/60 text-sm leading-relaxed mb-4">
              Mountain Home's trusted laundromat since 1963. 
              Fast, fresh, and clean laundry service for 
              residential and commercial customers.
            </p>
            <span className="inline-block text-poly-blue-light text-sm font-semibold mb-4">
              Fresh. Fast. Clean.
            </span>
            <a
              href={FACEBOOK_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-white/60 hover:text-[#1877F2] transition-colors text-sm"
              data-testid="footer-facebook"
              aria-label="Facebook"
            >
              <Facebook className="w-5 h-5" />
              Follow Us on Facebook
            </a>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-heading font-bold text-white text-base mb-4">Quick Links</h3>
            <nav className="space-y-2.5">
              {quickLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => scrollTo(e, link.href)}
                  className="block text-white/60 hover:text-poly-blue-light text-sm transition-colors hover:translate-x-1 transform duration-200"
                  data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.label}
                </a>
              ))}
            </nav>
          </div>

          {/* Services */}
          <div>
            <h3 className="font-heading font-bold text-white text-base mb-4">Services</h3>
            <div className="space-y-2.5">
              {serviceLinks.map((service, i) => (
                <a
                  key={i}
                  href="#services"
                  onClick={(e) => scrollTo(e, '#services')}
                  className="block text-white/60 hover:text-poly-blue-light text-sm transition-colors hover:translate-x-1 transform duration-200"
                >
                  {service}
                </a>
              ))}
            </div>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-heading font-bold text-white text-base mb-4">Contact</h3>
            <div className="space-y-4">
              <a href="tel:8704254295" className="flex items-center gap-3 text-white/60 hover:text-poly-blue-light transition-colors">
                <Phone className="w-4 h-4 shrink-0" />
                <span className="text-sm">(870) 425-4295</span>
              </a>
              <a href="mailto:polyclean2024@gmail.com" className="flex items-center gap-3 text-white/60 hover:text-poly-blue-light transition-colors">
                <Mail className="w-4 h-4 shrink-0" />
                <span className="text-sm">polyclean2024@gmail.com</span>
              </a>
              <div className="flex items-start gap-3 text-white/60">
                <MapPin className="w-4 h-4 shrink-0 mt-0.5" />
                <span className="text-sm">715 South Street<br/>Mountain Home, AR 72653</span>
              </div>
              <div className="flex items-start gap-3 text-white/60">
                <Clock className="w-4 h-4 shrink-0 mt-0.5" />
                <div className="text-sm">
                  <span className="block">Mon-Fri: 8AM - 5PM</span>
                  <span className="block">Sat: 8AM - 4PM</span>
                  <span className="block">Sun: Closed</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-white/40 text-sm">
            &copy; {new Date().getFullYear()} Poly Clean Center Laundromat. All rights reserved.
          </p>
          <button
            onClick={scrollToTop}
            className="flex items-center gap-2 text-white/40 hover:text-poly-blue-light text-sm transition-colors group"
            data-testid="back-to-top"
            aria-label="Back to top"
          >
            <ArrowUp className="w-4 h-4 group-hover:-translate-y-1 transition-transform" />
            Back to Top
          </button>
        </div>
      </div>

      {/* Extra bottom padding for mobile CTA bar */}
      <div className="h-20 lg:hidden" />
    </footer>
  );
}
